/* eslint-disable react/jsx-props-no-spreading */
import React, { ReactElement, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RiAlertFill } from 'react-icons/ri';
import { v4 as uuidv4 } from 'uuid';
import { skipToken } from '@reduxjs/toolkit/query';
import Modal from '../../../components/modal/modal';
import FormErrorLabel from '../../../components/form/formErrorLabel';
import FormTextInput from '../../../components/form/formTextInput';
import MultiselectList, {
  MultiselectContent
} from '../../../components/multiselectList/multiselectList';
import { useIdentityServiceListUsersQuery } from '../../../api/gen/identity';
import { useOfflineAlarmServiceCreateOfflineAlarmMutation } from '../../../api/enhanced/enhancedOfflineAlarms';
import {
  V1OfflineRecipient,
  V1SensorType
} from '../../../api/gen/offline-alarms';
import { useIdentityServiceListGroupsQuery } from '../../../api/enhanced/enhancedIdentity';
import Select from '../../../components/select/select';
import FormSelect, {
  FormSelectOption
} from '../../../components/form/formSelect';
import { useBuildingServiceListBuildingsQuery } from '../../../api/enhanced/enhancedBuildings';
import LoadingSpinner from '../../../components/spinner/loadingSpinner';

interface CreateAlarmFormValues {
  description: string;
  sensorType: string;
}

interface CreateOfflineAlarmModalProps {
  closeHandler: () => void;
  isOpen: boolean;
}

function CreateOfflineAlarmModal({
  closeHandler,
  isOpen
}: CreateOfflineAlarmModalProps): ReactElement {
  const methods = useForm<CreateAlarmFormValues>();
  const { t } = useTranslation();
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedUsers, setSelectedUsers] = useState<MultiselectContent[]>([]);
  const [selectedBuildings, setSelectedBuildings] = useState<
    MultiselectContent[]
  >([]);
  const [groupBuildings, setGroupBuildings] = useState<MultiselectContent[]>(
    []
  );
  const [alarmUsers, setAlarmUsers] = useState<MultiselectContent[]>([]);

  const [createAlarm, { isLoading, isError, isSuccess, reset }] =
    useOfflineAlarmServiceCreateOfflineAlarmMutation();

  const { data: groupData, isLoading: groupLoading } =
    useIdentityServiceListGroupsQuery({});

  const { data: buildingData } = useBuildingServiceListBuildingsQuery(
    selectedGroup != null && selectedGroup !== ''
      ? {
          groupId: selectedGroup
        }
      : skipToken
  );

  const { data } = useIdentityServiceListUsersQuery({
    sortBy: 'user_name:ASC'
  });

  useEffect(() => {
    if (buildingData != null && buildingData.buildings != null) {
      const content: MultiselectContent[] = [];
      buildingData.buildings.forEach((building) => {
        const foundElement = selectedBuildings.find(
          (element) => element.key === (building.id as string)
        );
        content.push({
          selected: foundElement != null ? foundElement.selected : false,
          key: building.id as string,
          label: building.description as string
        });
      });
      setGroupBuildings(content);
    }
  }, [buildingData, selectedBuildings]);

  useEffect(() => {
    if (
      groupData != null &&
      groupData.groups != null &&
      groupData.groups.length > 0
    ) {
      setSelectedGroup(groupData.groups[0].groupId as string);
    }
  }, [groupData]);

  useEffect(() => {
    if (data != null && data.users != null && data.users.length > 0) {
      const content: MultiselectContent[] = [];
      data.users.forEach((user) => {
        const selected = selectedUsers.some(
          (currUser) => currUser.key === user.userId && currUser.selected
        );
        if (user.isAdmin || user.isTechnician)
          content.push({
            selected,
            key: user.userId as string,
            label: `${user.userName as string} - ${user.mail as string}`
          });
      });
      setAlarmUsers(content);
    }
  }, [data, selectedUsers]);

  const onSubmit = (formData: CreateAlarmFormValues): void => {
    const alarmId = uuidv4();
    const recipients: V1OfflineRecipient[] = [];
    const buildingIds: string[] = [];
    if (data != null && data.users != null && selectedUsers.length > 0) {
      data.users.forEach((user) => {
        const selected = selectedUsers.some(
          (currUser) => currUser.key === user.userId && currUser.selected
        );
        if (selected) {
          recipients.push({
            id: uuidv4(),
            alarmId,
            userId: user.userId,
            mail: user.mail
          });
        }
      });
    }

    if (
      buildingData != null &&
      buildingData.buildings != null &&
      selectedBuildings.length > 0
    ) {
      selectedBuildings.forEach((building) => {
        if (building.selected) {
          buildingIds.push(building.key);
        }
      });
    }

    createAlarm({
      v1CreateOfflineAlarmReq: {
        alarm: {
          id: alarmId,
          description: formData.description,
          sensorType: formData.sensorType as V1SensorType,
          recipients,
          buildings: buildingIds
        }
      }
    });
  };

  useEffect(() => {
    if (isSuccess) {
      reset();
      methods.reset();
      setSelectedBuildings([]);
      setSelectedGroup('');
      setSelectedUsers([]);
      setGroupBuildings([]);
      closeHandler();
    }
  }, [isSuccess, methods, reset, closeHandler]);

  const close = (): void => {
    closeHandler();
    methods.reset();
    setSelectedBuildings([]);
    setSelectedGroup('');
    setGroupBuildings([]);
    setSelectedUsers([]);
    reset();
  };

  return (
    <FormProvider {...methods}>
      <Modal
        isOpen={isOpen}
        closeHandler={() => {
          close();
        }}
        title="Offlinealarm anlegen"
      >
        <div className="w-full flex flex-col">
          {isError && (
            <FormErrorLabel
              className="mb-3"
              icon={<RiAlertFill size={20} />}
              label={t('features.alarms.modals.create.error')}
            />
          )}

          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <FormTextInput
              id="description"
              label={t('features.alarms.modals.create.description.label')}
              errorMsg={
                t('features.alarms.modals.create.description.error') as string
              }
              placeholder={
                t('features.alarms.modals.create.description.label') as string
              }
              disabled={isLoading}
              required
            />

            <FormSelect
              id="sensorType"
              label="Sensortyp"
              options={[
                {
                  description: 'Falle',
                  value: 'SENSOR_TYPE_TRAP'
                },
                {
                  description: 'Temperatur',
                  value: 'SENSOR_TYPE_TEMP'
                },
                {
                  description: 'Kohlenstoffdioxid',
                  value: 'SENSOR_TYPE_CARBON_DIOXIDE'
                },
                {
                  description: 'Tür',
                  value: 'SENSOR_TYPE_DOOR'
                }
              ]}
            />

            <div>
              <div className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-2">
                {t('features.alarms.modals.create.users')}
              </div>
              <MultiselectList
                className="max-h-40 overflow-auto"
                selectHandler={(id: string, checkedState: boolean) => {
                  const itemExists = selectedUsers.some(
                    (item) => item.key === id
                  );

                  if (itemExists) {
                    setSelectedUsers(
                      selectedUsers.map((user) => {
                        if (user.key === id) {
                          return { ...user, selected: checkedState };
                        }

                        return user;
                      })
                    );
                  } else if (
                    data != null &&
                    data.users != null &&
                    data.users.length > 0
                  ) {
                    data.users.forEach((user) => {
                      if ((user.userId as string) === id) {
                        const content: MultiselectContent = {
                          key: user.userId as string,
                          label: user.userName as string,
                          selected: checkedState
                        };
                        setSelectedUsers((prev) => [...prev, content]);
                      }
                    });
                  }
                }}
                content={alarmUsers}
              />
            </div>

            {groupData != null &&
              groupData.groups != null &&
              groupData.groups.length > 0 && (
                <Select
                  label="Kunde"
                  value={selectedGroup}
                  changeHandler={(e) => setSelectedGroup(e)}
                  disabled={groupLoading}
                  options={groupData.groups.map((group): FormSelectOption => {
                    return {
                      value: group.groupId as string,
                      description: group.description as string
                    };
                  })}
                  placeholder="Kunde"
                />
              )}

            <div className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-2">
              Gebäude
            </div>
            <MultiselectList
              className="max-h-40 overflow-auto"
              selectHandler={(id: string, checkedState: boolean) => {
                const itemExists = selectedBuildings.some(
                  (item) => item.key === id
                );

                if (itemExists) {
                  setSelectedBuildings(
                    selectedBuildings.map((building) => {
                      if (building.key === id) {
                        return { ...building, selected: checkedState };
                      }

                      return building;
                    })
                  );
                } else if (
                  buildingData != null &&
                  buildingData.buildings != null &&
                  buildingData.buildings.length > 0
                ) {
                  buildingData.buildings.forEach((building) => {
                    if ((building.id as string) === id) {
                      const content: MultiselectContent = {
                        key: building.id as string,
                        label: building.description as string,
                        selected: checkedState
                      };
                      setSelectedBuildings((prev) => [...prev, content]);
                    }
                  });
                }
              }}
              content={groupBuildings}
            />

            <div className="flex items-end flex-col w-full mt-5">
              {!isLoading ? (
                <button
                  disabled={isLoading}
                  className=" rounded-lg shadow-md w-24 h-10 bg-blue-400 text-white"
                  type="submit"
                >
                  {t('modals.save')}
                </button>
              ) : (
                <div className="rounded-lg shadow-md w-24  h-10  p-4 bg-blue-400 text-white flex justify-center items-center">
                  <LoadingSpinner />
                </div>
              )}
            </div>
          </form>
        </div>
      </Modal>
    </FormProvider>
  );
}

export default CreateOfflineAlarmModal;
